import {Component, Inject, ChangeDetectionStrategy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {GuidLookupItem, GuidLookupService} from '../../services/guid-lookup/guid-lookup.service';
import {GuidInsertService} from '../../services/guid-insert/guid-insert.service';
import {MatSnackBar} from '@angular/material/snack-bar';

export interface GuidPickerDialogData {
  baseTable: string;
  fieldKey: string;
}

@Component({
    selector: 'app-guid-picker-dialog',
    templateUrl: './guid-picker-dialog.component.html',
    styleUrls: ['./guid-picker-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GuidPickerDialogComponent {
  fieldKey = '';
  baseTable = '';
  sourceKey = '';
  query = '';

  sources = this.lookupService.listSources();
  results: GuidLookupItem[] = [];
  selected: GuidLookupItem | null = null;
  loading = false;
  errorMessage = '';

  constructor(
    private dialogRef: MatDialogRef<GuidPickerDialogComponent>,
    private lookupService: GuidLookupService,
    private insertService: GuidInsertService,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: GuidPickerDialogData,
  ) {
    this.fieldKey = data.fieldKey || '';
    this.baseTable = data.baseTable || '';

    const resolved = this.lookupService.resolveSourceKey(this.fieldKey, this.baseTable);
    this.sourceKey = resolved || (this.sources[0] ? this.sources[0].key : '');

    this.load();
  }

  close(): void {
    this.dialogRef.close();
  }

  onFieldKeyChange(): void {
    const resolved = this.lookupService.resolveSourceKey(this.fieldKey, this.baseTable);
    if (resolved) {
      this.sourceKey = resolved;
    }
    this.load();
  }

  onSourceChange(): void {
    this.load();
  }

  onQueryChange(): void {
    this.load();
  }

  select(item: GuidLookupItem): void {
    this.selected = item;
  }

  insertItem(item: GuidLookupItem): void {
    this.selected = item;
    this.insertSelected();
  }

  async insertSelected(): Promise<void> {
    if (!this.selected) {
      return;
    }
    const ok = this.insertService.insertText(this.selected.id);
    if (ok) {
      this.snackBar.open('Inserted GUID', 'Close', { duration: 1500 });
      this.dialogRef.close();
      return;
    }
    const copied = await this.insertService.copyToClipboard(this.selected.id);
    this.snackBar.open(copied ? 'Copied GUID to clipboard' : 'Unable to insert GUID', 'Close', { duration: 2000 });
    this.dialogRef.close();
  }

  async copySelected(): Promise<void> {
    if (!this.selected) {
      return;
    }
    const copied = await this.insertService.copyToClipboard(this.selected.id);
    this.snackBar.open(copied ? 'Copied GUID to clipboard' : 'Unable to copy GUID', 'Close', { duration: 2000 });
  }

  trackById(_index: number, item: GuidLookupItem): string {
    return item.id;
  }

  private async load(): Promise<void> {
    if (!this.sourceKey) {
      this.results = [];
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    try {
      this.results = await this.lookupService.search(this.sourceKey, this.query);
    } catch (err) {
      this.errorMessage = typeof err === 'string' ? err : 'Unable to load results.';
    } finally {
      this.loading = false;
    }
  }
}
